/* ============================================================================
   Live competition feed.

   Wire protocol (server to client, one JSON object per frame):

     { "type": "snapshot",  "data": { portfolios, positions, trades, thoughts,
                                      equity, meta } }   full state, on connect
     { "type": "trade",     "data": Trade }              append to the tape
     { "type": "thought",   "data": Thought }            append to the stream
     { "type": "positions", "data": Position[] }         replace all open positions
     { "type": "portfolio", "data": Portfolio }          replace one agent
     { "type": "equity",    "data": EquitySample }       append one curve sample
     { "type": "meta",      "data": Partial<CompetitionMeta> }

   Ranking, ROI and house scoring are derived here from portfolios, so the
   backend never has to agree with the UI about how the table is sorted.

   Prices arrive from a separate adapter (see priceFeed.ts) because exchange
   data and agent data have completely different reliability characteristics:
   one dropping should not blank the other.
   ========================================================================== */

import { RUNTIME } from '../../config/runtime'
import type {
  CompetitionState,
  EquitySample,
  FeedStatus,
  Portfolio,
  Position,
  Thought,
  Trade,
} from '../../core/types'
import type { DataSource, StateStore } from '../DataSource'
import { deriveHouses, deriveLeaderboard } from '../derive'
import { createPriceFeed, type PriceFeed } from './priceFeed'

type Frame =
  | { type: 'snapshot'; data: Partial<CompetitionState> }
  | { type: 'trade'; data: Trade }
  | { type: 'thought'; data: Thought }
  | { type: 'positions'; data: Position[] }
  | { type: 'portfolio'; data: Portfolio }
  | { type: 'equity'; data: EquitySample }
  | { type: 'meta'; data: Partial<CompetitionState['meta']> }

export class LiveDataSource implements DataSource {
  readonly id = 'live' as const
  private ws: WebSocket | null = null
  private prices: PriceFeed | null = null
  private stopped = false
  private retry = 0
  private lastFrameAt = 0
  private watchdog: number | null = null
  private running = false
  private prevRanks: Record<string, number> = {}

  constructor(private store: StateStore) {}

  start() {
    this.stopped = false
    this.setStatus('connecting')

    // Prices are independent of the round and connect immediately.
    this.prices = createPriceFeed()
    this.prices?.start((u) =>
      this.store.setState({
        quotes: u.quotes,
        meta: { ...this.store.getState().meta, prices: 'live', priceSource: u.source },
      }),
    )
  }

  /** Subscribe to the agent feed. The round is standby until this is called. */
  activate() {
    if (this.running) return
    this.running = true
    if (!RUNTIME.live.wsUrl) {
      console.warn('[utc] VITE_COMPETITION_WS is not set: no agent feed to connect to.')
      this.setStatus('offline')
      return
    }
    this.store.setState({ meta: { ...this.store.getState().meta, phase: 'running' } })
    void this.warmStart()
    this.connect()
    this.watchdog = window.setInterval(() => {
      if (!this.lastFrameAt) return
      if (Date.now() - this.lastFrameAt > RUNTIME.live.stallMs) this.setStatus('stalled')
    }, 2_000)
  }

  stop() {
    this.stopped = true
    this.ws?.close()
    this.ws = null
    this.prices?.stop()
    this.prices = null
    this.running = false
    if (this.watchdog !== null) window.clearInterval(this.watchdog)
    this.watchdog = null
  }

  /** Optional REST warm start, so a late joiner is not staring at an empty grid. */
  private async warmStart() {
    if (!RUNTIME.live.restUrl) return
    try {
      const res = await fetch(`${RUNTIME.live.restUrl.replace(/\/$/, '')}/snapshot`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      this.applySnapshot((await res.json()) as Partial<CompetitionState>)
    } catch (err) {
      console.warn('[utc] snapshot bootstrap failed, waiting for the socket instead', err)
    }
  }

  private connect() {
    if (this.stopped) return
    const ws = new WebSocket(RUNTIME.live.wsUrl)
    this.ws = ws

    ws.onopen = () => {
      this.retry = 0
      this.lastFrameAt = Date.now()
      this.setStatus('live')
    }
    ws.onmessage = (ev) => {
      this.lastFrameAt = Date.now()
      let frame: Frame
      try {
        frame = JSON.parse(ev.data as string) as Frame
      } catch {
        console.warn('[utc] dropped an unparseable frame')
        return
      }
      this.apply(frame)
      if (this.store.getState().meta.status !== 'live') this.setStatus('live')
    }
    ws.onerror = () => ws.close()
    ws.onclose = () => {
      if (this.stopped) return
      this.setStatus('offline')
      this.retry += 1
      const wait = Math.min(30_000, RUNTIME.live.reconnectBaseMs * 2 ** this.retry)
      window.setTimeout(() => this.connect(), wait)
    }
  }

  private apply(frame: Frame) {
    const s = this.store.getState()
    switch (frame.type) {
      case 'snapshot':
        this.applySnapshot(frame.data)
        break
      case 'trade':
        this.store.setState({ trades: cap([...s.trades, frame.data], RUNTIME.maxTrades) })
        break
      case 'thought':
        this.store.setState({ thoughts: cap([...s.thoughts, frame.data], RUNTIME.maxThoughts) })
        break
      case 'positions':
        this.store.setState({ positions: frame.data })
        break
      case 'portfolio': {
        const portfolios = { ...s.portfolios, [frame.data.agentId]: frame.data }
        this.store.setState({ portfolios, ...this.standings(portfolios) })
        break
      }
      case 'equity':
        this.store.setState({
          equity: cap([...s.equity, frame.data], RUNTIME.maxEquitySamples),
        })
        break
      case 'meta':
        this.store.setState({ meta: { ...s.meta, ...frame.data, source: 'live' } })
        break
    }
  }

  private applySnapshot(data: Partial<CompetitionState>) {
    const s = this.store.getState()
    const portfolios = data.portfolios ?? s.portfolios
    this.store.setState({
      ...data,
      portfolios,
      meta: { ...s.meta, ...(data.meta ?? {}), source: 'live', status: 'live', phase: 'running' },
      ...this.standings(portfolios),
    })
  }

  private standings(portfolios: Record<string, Portfolio>) {
    const leaderboard = deriveLeaderboard(portfolios, this.prevRanks)
    const changed = leaderboard.some((r) => this.prevRanks[r.agentId] !== r.rank)
    if (changed) {
      const next: Record<string, number> = {}
      for (const r of leaderboard) next[r.agentId] = r.rank
      this.prevRanks = next
    }
    return { leaderboard, houses: deriveHouses(portfolios) }
  }

  private setStatus(status: FeedStatus) {
    const meta = this.store.getState().meta
    if (meta.status === status) return
    this.store.setState({ meta: { ...meta, status, source: 'live' } })
  }
}

const cap = <T>(xs: T[], max: number): T[] => (xs.length > max ? xs.slice(xs.length - max) : xs)
